import { useState, useEffect, FormEvent, RefObject } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import Input from '@gym-atoms/Input/Input';
import Dropdown, { DropdownChangeEvent, SelectItem } from '@gym-atoms/Dropdown/Dropdown';
import Button, { ButtonSizes, ButtonVariants } from '@gym-atoms/Button/Button';
import { useForm } from 'react-hook-form';
import Alert, { AlertType, createAlert } from '@gym-atoms/Alert/Alert';
import {
  AddSiteLocationProgramAccessCategoryInput,
  GymChainAccessCategoryInput,
  LambdaResponse,
  RelatedProgram,
  ProgramAccessCategorySite,
  AddProgramAccessCategoryInput,
  GetSiteLocationProgramAccessCategoriesQuery,
  SiteLocationProgramAccessCategory
} from '@gym-src/API';
import { API } from 'aws-amplify';
import {
  addGymChainAccessCategory,
  addSiteLocationProgramAccessCategory,
  addProgramAccessCategory,
  modifyGymChainAccessCategory,
  removeProgramAccessCategory,
  removeSiteLocationProgramAccessCategory,
  updateSiteLocationProgramAccessCategoryGroupId
} from '@gym-graphql/mutations';
import { useAppDispatch, useAppSelector } from '@gym-redux/store';
import DialogBox, { DialogBoxVariants } from '@gym-atoms/Dialog/DialogBox';
import Text from '@gym-atoms/Text/Text';
import { GymChainAccessCategory, partialGymType } from '@gym-particles/types/models';
import { UserRole } from '@gym-particles/types/User';
import { getPartialGymsOfGymChain } from '@gym-particles/queries';
import Loader from '@gym-atoms/Loader/Loader';
import {
  getAllPrograms,
  getProgramAccessCategories,
  getSiteLocationProgramAccessCategories
} from '@gym-graphql/queries';
import { GymChainProgramAccessCategory } from '../../../particles/types/GymChainProgramAccessCategory';
import { fetchGymChainProgramAccessCategory } from '../../../redux/slices/gymChainProgramAccessCategorySlice';

const GymChainProgramAccessCategoryInfoTemplate = (
  props: GymChainProgramAccessCategoryInfoTemplateProps
) => {
  const { t } = useTranslation();
  const history = useHistory();
  const location = useLocation<{ from?: string; gymId?: number; accessCategoryTab?: string }>();
  const { gymChainProgramAccessCategoryId, chainId, gymId } = useParams<{
    gymChainProgramAccessCategoryId: string;
    chainId: string;
    gymId?: string;
  }>();
  const dispatch = useAppDispatch();
  const [failedDialogVisible, setFailedDialogVisible] = useState(false);
  const [programsDropDownItems, setProgramsDropDownItems] = useState<Array<SelectItem>>([]);
  const [locationsDropDownItems, setLocationsDropDownItems] = useState<Array<SelectItem>>([]);
  const isAccessCategoriesLoading = useAppSelector(
    (state) => state.accessCategory.isAccessCategoriesLoading
  );
  const [defaultSelectedPrograms, setDefaultSelectedPrograms] = useState<
    Array<ProgramAccessCategorySite>
  >([]);
  const [defaultSelectedLocationPrograms, setDefaultSelectedLocationPrograms] = useState<
    SiteLocationProgramAccessCategory[]
  >([]);
  const [isDeleteDialogVisible, setIsDeleteDialogVisible] = useState(false);
  const userId = useAppSelector((state) => state.user.userId);
  const userRole = useAppSelector((state) => state.user.userRole);
  const [isInserted, setIsInserted] = useState(false);
  const [confirmDialogVisible, setConfirmDialogVisible] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState<FormInputs>();
  const [schedulesOnly, setSchedulesOnly] = useState(false);
  const [sortField, setSortField] = useState('createdDate');
  const [sortOrder, setSortOrder] = useState(-1);
  const [offset, setOffset] = useState(0);
  const [pageSize, setPageSize] = useState(1000);
  const [searchTerm, setSearchTerm] = useState('');
  const locationGymId = location.state?.gymId || (gymId ? +gymId : undefined);
  const isLocationProgramAccessCategory = Number.isFinite(locationGymId);

  const getBackPath = () => {
    if (props.backPath) {
      return props.backPath;
    }

    if (location.state?.from === 'chain' || !locationGymId) {
      return `/gymChains/${chainId}/gymChainAccessCategories`;
    }

    return `/gymChains/${chainId}/gyms/${locationGymId}/gymAccessCategories`;
  };

  const getBackState = () => ({ accessCategoryTab: 'bookings' });

  const getTranslationKey = (e: string): string => {
    return props.type === 'new' ? 'ADD_NEW_ACCESS_CATEGORY.' + e : 'MODIFY_ACCESS_CATEGORY.' + e;
  };

  type FormInputs = {
    id?: number;
    siteId?: number;
    name?: string;
    gender?: string;
    age?: number;
    schedulesOnly?: string;
    status?: string;
    programs?: Array<number>;
    siteLocationIds?: Array<string>;
    arxAccessCategoryId?: string;
  };

  const defaultValues: FormInputs = {
    id: props.programAccessCategory?.id || 0,
    siteId: props.programAccessCategory?.siteId || 0,
    name: props.programAccessCategory?.name || '',
    gender: props.programAccessCategory?.gender || '',
    age: props.programAccessCategory?.age || 0,
    schedulesOnly: props.programAccessCategory?.schedulesOnly ? 'true' : 'false',
    status: props.programAccessCategory?.status || '',
    programs: [],
    siteLocationIds: [],
    arxAccessCategoryId: props.programAccessCategory?.arxAccessCategoryId || ''
  };

  type SiteAccessCategoryChange = {
    field: string;
    previous: string;
    next: string;
  };

  const formatFieldValue = (value: string | number | undefined): string => {
    if (value === undefined || value === '') {
      return '-';
    }

    return String(value);
  };

  const getSiteAccessCategoryChanges = (currentValues: FormInputs): SiteAccessCategoryChange[] => {
    const fields: Array<{
      field: keyof Pick<FormInputs, 'name' | 'gender' | 'age' | 'status'>;
      label: string;
    }> = [
      { field: 'name', label: 'Name' },
      { field: 'gender', label: 'Gender' },
      { field: 'age', label: 'Age' },
      { field: 'status', label: 'Status' }
    ];

    return fields
      .map(({ field, label }) => ({
        field: label,
        previous: formatFieldValue(defaultValues[field]),
        next: formatFieldValue(currentValues[field])
      }))
      .filter((change) => change.previous !== change.next);
  };

  const genderDropDownItems = [
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_MALE')),
      value: 'male'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_FEMALE')),
      value: 'female'
    },
    {
      label: t(getTranslationKey('FORM_GENDER_DROPDOWN_NA')),
      value: 'n/a'
    }
  ];

  const inputFieldChangeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof GymChainAccessCategoryInput, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
  };

  const [formValues, setFormValues] = useState(defaultValues);

  const { register, handleSubmit, setValue, errors } = useForm<FormInputs>({
    defaultValues: defaultValues
  });

  const DeleteDialogFooter = () => {
    return (
      <div>
        <Button
          label={t('COMMON.OK')}
          variant={ButtonVariants.basic}
          onClick={() => setIsDeleteDialogVisible(false)}
        />
      </div>
    );
  };
  const RemovableGymDialog = () => {
    return (
      <div>
        <DialogBox
          dialogVisible={isDeleteDialogVisible}
          variant={DialogBoxVariants.long}
          dialogDismissableMask={true}
          onHideCallback={() => setIsDeleteDialogVisible(false)}
          dialogFooter={<DeleteDialogFooter />}
          dialogClosable={false}
          dialogHeader={t('MEMBERSHIP_LIST.TABLE.DELETE_MODAL_HEADER')}
          content={<Text>{t('MEMBERSHIP_LIST.TABLE.DELETE_GYM_MODAL_CANNOT')}</Text>}
        />
      </div>
    );
  };

  const getGymPrograms = async () => {
    try {
      console.log('defaultSelectedPrograms', defaultSelectedPrograms);
      const siteProgramsResponse = await (API.graphql({
        query: getAllPrograms,
        variables: {
          siteId: chainId
        }
      }) as Promise<{
        data: { getAllPrograms: RelatedProgram[] };
      }>);
      const siteProgramsDropDownItems =
        siteProgramsResponse.data.getAllPrograms
          .map((data) => {
            return {
              label: data.programName + ' (' + data.importedId + ')' || '',
              value: data.id
            };
          })
          .filter((item) => item !== undefined) || [];
      console.log({ siteProgramsDropDownItems });
      setProgramsDropDownItems(siteProgramsDropDownItems);
      props.type === 'modify' && getDefaultSelectedPrograms();
      console.log({ formValues });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getSiteLocations = async () => {
    if (isLocationProgramAccessCategory) {
      return;
    }

    try {
      const response = await (API.graphql({
        query: getPartialGymsOfGymChain,
        variables: {
          id: chainId,
          offset: 0,
          pageSize: 1000,
          sortField: 'gymName',
          sortOrder: 'ASC',
          userId: userRole === UserRole.SYSTEM_ADMIN ? 0 : userId
        }
      }) as Promise<{
        data: { getGymsOfGymChain: { items: partialGymType[] } };
      }>);

      const locationItems = response.data.getGymsOfGymChain.items
        .filter((gym) => gym.gymId)
        .map((gym) => ({
          label: gym.gymName,
          value: String(gym.gymId)
        }));

      setLocationsDropDownItems(locationItems);
      props.type === 'modify' && getDefaultSelectedLocations(locationItems);
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const getDefaultSelectedLocations = async (locationItems: SelectItem[]) => {
    try {
      const locationProgramResponses = await Promise.all(
        locationItems.map(async (locationItem) => {
          const response = await (API.graphql({
            query: getSiteLocationProgramAccessCategories,
            variables: {
              siteLocationId: Number(locationItem.value)
            }
          }) as Promise<{ data: GetSiteLocationProgramAccessCategoriesQuery }>);

          return response.data.getSiteLocationProgramAccessCategories?.items || [];
        })
      );

      setDefaultSelectedLocationPrograms(
        locationProgramResponses
          .flat()
          .filter(
            (item): item is SiteLocationProgramAccessCategory =>
              Boolean(item) &&
              item?.accessCategoryId === +gymChainProgramAccessCategoryId &&
              Boolean(item?.siteLocationProgramAccessCategoryId)
          )
      );
    } catch (error) {
      console.error('Failed to fetch selected location program access categories', error);
    }
  };

  const getDefaultSelectedPrograms = async () => {
    console.log('defaultSelectedPrograms', gymChainProgramAccessCategoryId);
    try {
      if (isLocationProgramAccessCategory) {
        const defaultProgramsResponse = await (API.graphql({
          query: getSiteLocationProgramAccessCategories,
          variables: {
            siteLocationId: Number(locationGymId)
          }
        }) as Promise<{ data: GetSiteLocationProgramAccessCategoriesQuery }>);
        const locationPrograms = (
          defaultProgramsResponse.data.getSiteLocationProgramAccessCategories?.items || []
        ).filter(
          (item): item is SiteLocationProgramAccessCategory =>
            Boolean(item) && item?.accessCategoryId === +gymChainProgramAccessCategoryId
        );

        setDefaultSelectedPrograms(
          locationPrograms.map((program) => ({
            __typename: 'ProgramAccessCategorySite',
            programAccessCategoryId: program.siteLocationProgramAccessCategoryId,
            programArxAccessCategoryId: program.arxAccessCategoryId,
            programId: program.programId,
            programName: program.programName,
            programScheduleType: program.scheduleType,
            programImportedId: program.programImportedId,
            programAccessCategoryStatus: program.status,
            isDeleted: false
          }))
        );
        return;
      }

      const defaultProgramsResponse = await (API.graphql({
        query: getProgramAccessCategories,
        variables: {
          accessCategoryId: +gymChainProgramAccessCategoryId
        }
      }) as Promise<{
        data: { getProgramAccessCategories: ProgramAccessCategorySite[] };
      }>);
      console.log({ selectedPrograms: defaultProgramsResponse.data });

      setDefaultSelectedPrograms(defaultProgramsResponse.data.getProgramAccessCategories);
    } catch (error) {
      console.error('Failed to fetch selected programs', error);
    }
  };

  useEffect(() => {
    getGymPrograms();
    getSiteLocations();
    dispatch(
      fetchGymChainProgramAccessCategory({
        siteId: props.programAccessCategory?.siteId || 0
      })
    );
  }, []);

  const programsDropdownHandler = async (e: DropdownChangeEvent) => {
    setValue('programs', e.value);
    setFormValues({ ...formValues, programs: e.value });
  };

  const locationsDropdownHandler = async (e: DropdownChangeEvent) => {
    setValue('siteLocationIds', e.value);
    setFormValues({ ...formValues, siteLocationIds: e.value });
  };

  useEffect(() => {
    const selectedLocationIds = Array.from(
      new Set(
        defaultSelectedLocationPrograms
          .map((program) => program.siteLocationId)
          .filter((siteLocationId): siteLocationId is number => Boolean(siteLocationId))
          .map((siteLocationId) => String(siteLocationId))
      )
    );

    setFormValues({
      ...formValues,
      id: props.programAccessCategory?.id || 0,
      siteId: props.programAccessCategory?.siteId || 0,
      name: props.programAccessCategory?.name || '',
      gender: props.programAccessCategory?.gender || '',
      schedulesOnly: props.programAccessCategory?.schedulesOnly ? 'true' : 'false',
      status: props.programAccessCategory?.status || '',
      programs: defaultSelectedPrograms.map((program) => program.programId),
      siteLocationIds: isLocationProgramAccessCategory
        ? [String(locationGymId)]
        : selectedLocationIds,
      arxAccessCategoryId: props.programAccessCategory?.arxAccessCategoryId || ''
    });
    setValue('name', props.programAccessCategory?.name);
    setValue('siteId', props.programAccessCategory?.siteId);
    setValue('gender', props.programAccessCategory?.gender);
    setValue('age', props.programAccessCategory?.age);
    setValue('schedulesOnly', props.programAccessCategory?.schedulesOnly);
    setValue('status', props.programAccessCategory?.status);
    console.log({ programs: defaultSelectedPrograms.map((program) => program.programId) });
    console.log({ formValues: formValues.programs });
    setValue(
      'programs',
      defaultSelectedPrograms.map((program) => program.programId)
    );
    setValue(
      'siteLocationIds',
      isLocationProgramAccessCategory ? [String(locationGymId)] : selectedLocationIds
    );
    setValue('arxAccessCategoryId', props.programAccessCategory?.arxAccessCategoryId);
  }, [props, defaultSelectedPrograms, defaultSelectedLocationPrograms]);

  const ActionFailedDialog = () => {
    return (
      <div>
        <DialogBox
          variant={DialogBoxVariants.basic}
          dialogVisible={failedDialogVisible}
          onHideCallback={() => setFailedDialogVisible(false)}
          dialogHeader={t(getTranslationKey('FORM_FAILURE_DIALOG_HEADER'))}
          dialogFooter={
            <Button
              label={t(getTranslationKey('FORM_FAILURE_DIALOG_BUTTON'))}
              onClick={() => setFailedDialogVisible(false)}
            />
          }
          dialogDismissableMask={true}
          dialogClosable={false}
          content={<Text>{t(getTranslationKey('FORM_FAILURE_DIALOG_BODY'))}</Text>}
        />
      </div>
    );
  };

  const removeLocationAccessCategory = async (
    programAccessCategoryId: number,
    siteLocationId?: number
  ) => {
    try {
      if (isLocationProgramAccessCategory || siteLocationId) {
        await API.graphql({
          query: removeSiteLocationProgramAccessCategory,
          variables: {
            ids: [programAccessCategoryId],
            siteLocationId: siteLocationId || Number(locationGymId)
          }
        });
        return;
      }

      await API.graphql({
        query: removeProgramAccessCategory,
        variables: {
          id: programAccessCategoryId
        }
      });
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addLocationProgramAccessCategories = async (
    arxAccessCategoryId: string,
    programs: number[],
    addAccessCategoryResponse: GymChainAccessCategory,
    siteLocationIds: number[]
  ) => {
    await Promise.all(
      siteLocationIds.map(async (siteLocationId) => {
        const addSiteLocationProgramAccessCategoryInput: AddSiteLocationProgramAccessCategoryInput = {
          name: addAccessCategoryResponse.name.replace(/'/g, "''"),
          programIds: Array.isArray(programs) ? programs.map((id) => +id) : [programs],
          gender: addAccessCategoryResponse.gender,
          age: addAccessCategoryResponse.age,
          accessCategoryId: addAccessCategoryResponse.id,
          arxAccessCategoryId: arxAccessCategoryId,
          status: addAccessCategoryResponse.status,
          createdBy: userId,
          lastModifiedBy: userId,
          schedulesOnly: schedulesOnly,
          siteLocationId
        };

        const result = await (API.graphql({
          query: addSiteLocationProgramAccessCategory,
          variables: {
            input: addSiteLocationProgramAccessCategoryInput
          }
        }) as Promise<{
          data: { addSiteLocationProgramAccessCategory: LambdaResponse };
        }>);

        if (result.data.addSiteLocationProgramAccessCategory.error) {
          throw result.data.addSiteLocationProgramAccessCategory;
        }
      })
    );
  };

  const addProgramAccessCategories = async (
    arxAccessCategoryId: string,
    programs: number[],
    addAccessCategoryResponse: GymChainAccessCategory,
    siteLocationIds: number[] = []
  ) => {
    try {
      console.log({ programs });
      console.log({ addAccessCategoryResponse });
      if (isLocationProgramAccessCategory) {
        await addLocationProgramAccessCategories(
          arxAccessCategoryId,
          programs,
          addAccessCategoryResponse,
          [Number(locationGymId)]
        );
        return;
      }

      const addProgramAccessCategoryInput: AddProgramAccessCategoryInput = {
        name: addAccessCategoryResponse.name.replace(/'/g, "''"),
        programIds: Array.isArray(programs) ? programs.map((id) => +id) : [programs],
        gender: addAccessCategoryResponse.gender,
        age: addAccessCategoryResponse.age,
        accessCategoryId: addAccessCategoryResponse.id,
        arxAccessCategoryId: arxAccessCategoryId,
        status: addAccessCategoryResponse.status,
        createdBy: userId,
        lastModifiedBy: userId,
        programs: []
      };

      addProgramAccessCategoryInput.programs = addProgramAccessCategoryInput.programIds.map(
        (id) => programsDropDownItems.find((m) => m.value === id)?.label || ''
      );
      console.log({ addProgramAccessCategoryInput });

      try {
        const result = await (API.graphql({
          query: addProgramAccessCategory,
          variables: {
            input: addProgramAccessCategoryInput
          }
        }) as Promise<{
          data: { addProgramAccessCategory: LambdaResponse };
        }>);
        console.log(result);
        if (result.data.addProgramAccessCategory.error) {
          throw result.data.addProgramAccessCategory;
        }
        console.log(result);
      } catch (error) {
        setFailedDialogVisible(true);
        console.log(error);
      }

      if (siteLocationIds.length > 0) {
        await addLocationProgramAccessCategories(
          arxAccessCategoryId,
          programs,
          addAccessCategoryResponse,
          siteLocationIds
        );
      }
    } catch (error) {
      setFailedDialogVisible(true);
      console.log(error);
    }
  };

  const addSiteAccessCategory = async (data: FormInputs) => {
    console.log('addAccessCategory:', data);
    const addAccessCategoryInput: GymChainAccessCategoryInput = {
      siteId: +chainId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: +data.age || 0,
      schedulesOnly: false,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };
    console.log({ addAccessCategoryInput });

    try {
      const addAccessCategoryResponse = await (API.graphql({
        query: addGymChainAccessCategory,
        variables: {
          input: addAccessCategoryInput
        }
      }) as Promise<{
        data: { addGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ addAccessCategoryResponse });
      console.log(data.programs);

      if (data.programs && data.programs.length > 0) {
        await addProgramAccessCategories(
          data.arxAccessCategoryId || '',
          data.programs,
          addAccessCategoryResponse.data.addGymChainAccessCategory,
          (data.siteLocationIds || []).map((siteLocationId) => Number(siteLocationId))
        );
      }
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(getBackPath(), getBackState()), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  const modifyAccessCategory = async (data: FormInputs) => {
    console.log('modify', { data });
    const modifyGymChainAccessCategoryInput: GymChainAccessCategoryInput = {
      id: props.programAccessCategory.id,
      siteId: props.programAccessCategory.siteId || 0,
      name: data.name || '',
      gender: data.gender || '',
      age: data.age || 0,
      schedulesOnly: schedulesOnly,
      status: data.status || '',
      createdBy: userId,
      lastModifiedBy: userId
    };

    try {
      const modifyGymAccessCategoryResponse = await (API.graphql({
        query: modifyGymChainAccessCategory,
        variables: {
          input: modifyGymChainAccessCategoryInput
        }
      }) as Promise<{
        data: { modifyGymChainAccessCategory: GymChainAccessCategory };
      }>);
      console.log({ modifyGymAccessCategoryResponse });
      const addedPrograms = data.programs?.filter(
        (x: number) =>
          !defaultSelectedPrograms.map((y: ProgramAccessCategorySite) => y.programId).includes(+x)
      );
      const currentLocationIds = Array.from(
        new Set(
          defaultSelectedLocationPrograms
            .map((program) => program.siteLocationId)
            .filter((siteLocationId): siteLocationId is number => Boolean(siteLocationId))
        )
      );
      const selectedLocationIds = (data.siteLocationIds || []).map((siteLocationId) =>
        Number(siteLocationId)
      );
      const addedLocationIds = selectedLocationIds.filter(
        (siteLocationId) => !currentLocationIds.includes(siteLocationId)
      );
      const removedLocationIds = currentLocationIds.filter(
        (siteLocationId) => !selectedLocationIds.includes(siteLocationId)
      );
      console.log({ addedPrograms });

      if (addedPrograms && addedPrograms.length > 0) {
        await addProgramAccessCategories(
          data.arxAccessCategoryId || '',
          addedPrograms,
          modifyGymAccessCategoryResponse.data.modifyGymChainAccessCategory,
          selectedLocationIds
        );
      }

      const existingProgramsForAddedLocations = (data.programs || []).filter(
        (programId) => !(addedPrograms || []).includes(programId)
      );

      if (
        !isLocationProgramAccessCategory &&
        addedLocationIds.length > 0 &&
        existingProgramsForAddedLocations.length > 0
      ) {
        await addLocationProgramAccessCategories(
          data.arxAccessCategoryId || '',
          existingProgramsForAddedLocations,
          modifyGymAccessCategoryResponse.data.modifyGymChainAccessCategory,
          addedLocationIds
        );
      }

      await Promise.all(
        removedLocationIds.map(async (siteLocationId) => {
          const removedLocationPrograms = defaultSelectedLocationPrograms.filter(
            (program) =>
              program.siteLocationId === siteLocationId &&
              program.siteLocationProgramAccessCategoryId
          );

          await Promise.all(
            removedLocationPrograms.map((program) =>
              removeLocationAccessCategory(
                program.siteLocationProgramAccessCategoryId || 0,
                siteLocationId
              )
            )
          );
        })
      );

      const removedPrograms = defaultSelectedPrograms
        .map((y: ProgramAccessCategorySite) => y.programId)
        .filter((x: number) => !data.programs?.includes(Number(x)));
      console.log({ removedPrograms });

      await Promise.all(
        removedPrograms.map(async (g: number) => {
          const ProgramRemoved = defaultSelectedPrograms.find(
            (i: ProgramAccessCategorySite) => i.programId === g
          )?.programAccessCategoryId;
          console.log({ ProgramRemoved });

          if (ProgramRemoved && ProgramRemoved > 0) {
            await removeLocationAccessCategory(ProgramRemoved);
          }
        })
      );

      await Promise.all(
        removedPrograms.map(async (programId: number) => {
          const removedLocationPrograms = defaultSelectedLocationPrograms.filter(
            (program) =>
              program.programId === programId && program.siteLocationProgramAccessCategoryId
          );

          await Promise.all(
            removedLocationPrograms.map((program) =>
              removeLocationAccessCategory(
                program.siteLocationProgramAccessCategoryId || 0,
                program.siteLocationId || Number(locationGymId)
              )
            )
          );
        })
      );

      if (
        isLocationProgramAccessCategory &&
        data.arxAccessCategoryId !== props.programAccessCategory.arxAccessCategoryId
      ) {
        await API.graphql({
          query: updateSiteLocationProgramAccessCategoryGroupId,
          variables: {
            input: {
              siteLocationId: Number(locationGymId),
              accessCategoryId: props.programAccessCategory.id,
              programIds: data.programs || [],
              arxAccessCategoryId: data.arxAccessCategoryId || '',
              lastModifiedBy: userId
            }
          }
        });
      }
      createAlert(props.alertRef, {
        content: t(getTranslationKey('ALERT_MESSAGE_SUCCESS')),
        severity: 'success'
      });
      setTimeout(() => history.push(getBackPath(), getBackState()), 3000);
    } catch (e) {
      setFailedDialogVisible(true);
      console.log(e);
    }
  };

  //Form submission handler
  const onSubmitHandler = async (data: FormInputs) => {
    setIsInserted(true);
    try {
      if (props.type === 'new') {
        await addSiteAccessCategory(data);
      } else if (props.type === 'modify' && props.programAccessCategory) {
        const siteAccessCategoryChanges = getSiteAccessCategoryChanges(data);

        if (isLocationProgramAccessCategory && siteAccessCategoryChanges.length > 0) {
          setPendingSubmitData(data);
          setConfirmDialogVisible(true);
          return;
        }

        await modifyAccessCategory(data);
      }
    } finally {
      setIsInserted(false);
    }
  };

  const confirmSiteAccessCategoryChanges = async () => {
    if (!pendingSubmitData) {
      setConfirmDialogVisible(false);
      return;
    }

    try {
      setIsInserted(true);
      setConfirmDialogVisible(false);
      await modifyAccessCategory(pendingSubmitData);
      setPendingSubmitData(undefined);
    } finally {
      setIsInserted(false);
    }
  };

  useEffect(() => {
    register('name', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string,
      maxLength: { value: 255, message: 'required' as string }
    });
    register('gender');
    register('age');
    register('schedulesOnly');
    register('status', {
      required: t(getTranslationKey('FORM_REQUIRED_FIELD_ERROR')) as string
    });
    register('programs');
    register('siteLocationIds');
    register('arxAccessCategoryId');
  }, [register]);

  // TO-DO [Suggestion] - Keep these values in one place as enums.

  const dropdownChangeHandler = (e: DropdownChangeEvent) => {
    setValue(e.target.name as keyof GymChainAccessCategoryInput, e.value);
    setFormValues({ ...formValues, [e.target.name]: e.value });
  };
  const statusDropDownItems = [
    {
      label: t(getTranslationKey('FORM_STATUS_DROPDOWN_ACTIVE')),
      value: 'active'
    },
    { label: t(getTranslationKey('FORM_STATUS_DROPDOWN_INACTIVE')), value: 'inactive' }
  ];
  const statusDropdownHandler = (e: DropdownChangeEvent) => {
    setValue('status', e.value);
    setFormValues({ ...formValues, status: e.value });
  };

  const changeHandler = (e: FormEvent<HTMLInputElement>) => {
    setValue(e.currentTarget.name as keyof FormInputs, e.currentTarget.value);
    setFormValues({ ...formValues, [e.currentTarget.name]: e.currentTarget.value });
    console.log({ formValues });
  };
  const loaderElement = () => {
    return (
      <div className="loaderHeader">
        <div className="loaderContent">
          <Loader shape="rectangle" width="quarter" />
        </div>
        <div>
          <Loader shape="rectangle" width="full" />
        </div>
      </div>
    );
  };

  const ConfirmSiteAccessCategoryChangesDialog = () => {
    const changes = pendingSubmitData ? getSiteAccessCategoryChanges(pendingSubmitData) : [];

    return (
      <DialogBox
        variant={DialogBoxVariants.long}
        dialogVisible={confirmDialogVisible}
        onHideCallback={() => setConfirmDialogVisible(false)}
        dialogHeader={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_HEADER'))}
        dialogFooter={
          <div>
            <Button
              label={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => {
                setConfirmDialogVisible(false);
                setPendingSubmitData(undefined);
              }}
            />
            <Button
              label={t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_CONFIRM'))}
              onClick={confirmSiteAccessCategoryChanges}
              icon={isInserted ? 'pi-spinner pi-spin' : ''}
              disabled={isInserted}
            />
          </div>
        }
        dialogDismissableMask={true}
        dialogClosable={false}
        content={
          <div>
            <Text>{t(getTranslationKey('CONFIRM_SITE_ACCESS_CATEGORY_DIALOG_BODY'))}</Text>
            <ul>
              {changes.map((change) => (
                <li key={change.field}>
                  <span className="fw-bold">{change.field}:</span> {change.previous} {' -> '}
                  {change.next}
                </li>
              ))}
            </ul>
          </div>
        }
      />
    );
  };

  return (
    <div className="row">
      <div className="d-flex flex-row">
        <div className="col">
          <Alert refProp={props.alertRef} />
        </div>
      </div>
      {isAccessCategoriesLoading ? (
        [...Array(5)].map((element, index) => {
          return <div key={index}> {loaderElement()} </div>;
        })
      ) : (
        <form className="input-mb20" onSubmit={handleSubmit(onSubmitHandler)} noValidate>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_LABEL'))}
                variant="basic"
                name="name"
                value={formValues.name}
                onChange={inputFieldChangeHandler}
                error={errors.name ? true : false}
                errorMessage={errors.name?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_NAME_PLACEHOLDER'))}
                required={true}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_LABEL'))}
            </label>
            <Dropdown
              name="gender"
              options={genderDropDownItems}
              value={formValues.gender}
              onChange={dropdownChangeHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GENDER_PLACEHOLDER'))}
              error={errors.gender ? true : false}
            />
          </div>
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_LABEL'))}
                variant="basic"
                name="age"
                value={formValues.age > 0 ? formValues.age.toString() : ''}
                onChange={inputFieldChangeHandler}
                error={errors.age ? true : false}
                errorMessage={errors.age?.message}
                placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_AGE_PLACEHOLDER'))}
                required={false}
              />
            </div>
          </div>
          <div className="col-12">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_LABEL'))}
              <span className="p-error"> *</span>
            </label>
            <Dropdown
              name="status"
              options={statusDropDownItems}
              value={formValues.status}
              onChange={statusDropdownHandler}
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_STATUS_PLACEHOLDER'))}
              error={errors.status ? true : false}
            />
          </div>
          {!isLocationProgramAccessCategory && (
            <>
              <div className="mb25">
                <label className="fw-bold">{t(getTranslationKey('FORM_INPUT_LABEL_GYMS'))}</label>
                {locationsDropDownItems.length > 0 ? (
                  <Dropdown
                    key={`locations-count-${locationsDropDownItems.length}`}
                    chips={true}
                    name="siteLocationIds"
                    placeholder={t(getTranslationKey('FORM_INPUT_PLACEHOLDER_GYMS'))}
                    value={formValues.siteLocationIds || []}
                    options={locationsDropDownItems}
                    onChange={locationsDropdownHandler}
                    error={errors.siteLocationIds ? true : false}
                  />
                ) : (
                  <div className="p-inputtext p-disabled">
                    {t(getTranslationKey('LOADING_GYMS'))}
                  </div>
                )}
              </div>
              <div className="mb25">
                <h4>Selected Locations:</h4>
                {formValues.siteLocationIds?.map((id) => {
                  const matchingItem = locationsDropDownItems.find((obj) => obj.value === id);

                  return (
                    <span
                      key={id}
                      className="p-badge"
                      style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                      {matchingItem ? matchingItem.label : t('COMMON.LOADING')}
                    </span>
                  );
                })}
              </div>
            </>
          )}
          <div className="mb25">
            <label className="fw-bold">
              {t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_PROGRAM_LABEL'))}
            </label>
            <Dropdown
              key={programsDropDownItems.length > 0 ? 'loaded' : 'loading'}
              chips={true}
              name="programs"
              placeholder={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_PROGRAM_PLACEHOLDER'))}
              value={formValues.programs}
              options={programsDropDownItems}
              onChange={programsDropdownHandler}
              error={errors.programs ? true : false}
            />
          </div>
          {formValues.programs?.length > 0 && (
            <div className="mb25">
              <h4>Selected programs:</h4>
              <h3>{formValues.programs?.length} Programs selected</h3>
              <div>
                {formValues.programs?.map((name, index) => (
                  <span
                    key={index}
                    className="p-badge"
                    style={{ color: 'white', fontWeight: 'bold', marginRight: '6px' }}>
                    {programsDropDownItems.find((obj) => obj.value === name)?.label ||
                      t('COMMON.LOADING')}
                  </span>
                ))}
              </div>
            </div>
          )}
          <div className="row">
            <div className="col-12">
              <Input
                label={t(getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GROUP_ID_LABEL'))}
                variant="basic"
                name="arxAccessCategoryId"
                value={formValues.arxAccessCategoryId}
                onChange={inputFieldChangeHandler}
                error={errors.name ? true : false}
                errorMessage={errors.name?.message}
                placeholder={t(
                  getTranslationKey('FORM_INPUT_ACCESS_CATEGORY_GROUP_ID_PLACEHOLDER')
                )}
                required={false}
                disabled={formValues.programs.length === 0}
              />
            </div>
          </div>
          <div className="d-flex flex-row mt35 btn-min-w-110">
            <div className="mr15">
              <Button
                data-cy={'membership-info-temp-form-btn-save'}
                icon={isInserted ? 'pi-spinner pi-spin' : ''}
                label={t(getTranslationKey('FORM_BTN_LABEL_SAVE'))}
                size={ButtonSizes.medium}
                disabled={isInserted}
              />
            </div>
            <Button
              data-cy={'membership-info-temp-form-btn-cancel'}
              label={t(getTranslationKey('FORM_BTN_LABEL_CANCEL'))}
              variant={ButtonVariants.textonly}
              onClick={() => history.push(getBackPath(), getBackState())}
              disabled={isInserted}
            />
            <ActionFailedDialog />
            <RemovableGymDialog />
            <ConfirmSiteAccessCategoryChangesDialog />
          </div>
        </form>
      )}
    </div>
  );
};

export default GymChainProgramAccessCategoryInfoTemplate;

type GymChainProgramAccessCategoryInfoTemplateType = 'new' | 'modify';

export interface GymChainProgramAccessCategoryInfoTemplateProps {
  type: GymChainProgramAccessCategoryInfoTemplateType;
  programAccessCategory?: GymChainProgramAccessCategory;
  alertRef: RefObject<AlertType>;
  alertMessage: string;
  backPath?: string;
}
